import React, { forwardRef, RefObject, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import {
  AnnotationActionMenuPosition,
  AnnotationImageData,
  Coords,
  pageImageData,
  Point,
  PreviewAction,
  rotateAngleValue,
} from './Preview.types';
import useSessionUser from '../../hooks/useSessionUser';
import { getImageUnderPointer, getTargetPageImage, getTargetPageImageToInsertAnnotation } from './previewUtils';
import {
  addAnnotationOnOrgz,
  deleteAnnotationOnOrgz,
  updateAnnotationOnOrgz,
} from '../../services/exams/examTaskGroup/examTaskGroup.service';
import axios, { AxiosResponse } from 'axios';
import { useLayoutContext } from '../../Context/Layout.context';
import { fabric } from 'fabric';
import { MouseButtons } from '../../const/mouse';
import {
  AnnotationDefinition,
  AnnotationOnImage,
  AnnotationType,
  AnnotationDefinitionCore,
  convertAnnotationDefinitionToAnnotationDefinitionCore,
} from '../../models/annotation.types';
import { ExamScoringSchema, ExamTaskGroupSolutionScoreShelfResponse } from '../../models/assessment.types';
import { ActionMenuItem } from '../ActionsMenu/ActionsMenu.types';
import ActionsMenuItems from '../ActionsMenu/ActionsMenuItems.component';
import { Prompt } from '../Prompt/Prompt.component';
import { useScoring } from '../../Context/Scoring.context';
import { getCriterionFromTasks } from '../../Context/helpers';
import { Status } from '../../services/exams/assessmentSession/assessments.types';
import useAnnotationsKeyboard from '../../hooks/useAnnotationsKeyboard';
import {
  getAnnotationDefinitionCore,
  setupDynamicAnnotation,
  createNewAnnotationPositionBasedOnImage,
  beforeRemove,
  getAnnotationIdFromImg,
  removeAnnotationImagesFromCanvas,
  rotateAnnotation,
  getAnnotationWithUpdatedLayers,
} from './annotationsOnPageManagerService';
import { useAnnotations } from './useAnnotations';
import { useAnnotationsImageSetup } from './useAnnotationsImageSetup';
import { ANNOTATION_OBJECT_NAME, MINIMUM_ANNOTATION_SIZE } from './annotationsConsts';
import { useAnnotationCursor } from './useAnnotationCursor';
import { useDynamicCreaton } from './useDynamicCreaton';
import { useCanvasHandlers } from './useCanvasHandlers';

const ANNOTATION_UPDATE_KEYBOARD_DELAY = 550;

interface AnnotationsOnPageManagerProps {
  canAddAnnotation: boolean;
  topCanvasMargin: number;
  onActionTrigger: (action: PreviewAction) => void;
  onAddAnnotation: (annotation: AnnotationOnImage) => void;
  onBeforeAddAnnotation: () => void;
  onAnnotationChanged: (annotations: AnnotationOnImage[]) => void;
  onAnnotationDeleted: (annotation: AnnotationOnImage) => void;
  onAnnotationsRefresh: (examTaskGroupSolutionScoreVersionId?: string) => void;
  onAnnotationDeactivate: () => void;
  onCommentChanged?: () => void;
  getPopoverCoord: (left: number, top: number, zoom: number, offset: Coords) => Point;
  pageAreAddedToCanvas: boolean;
  examSheetPagesLoaded: boolean;
  imagesCount: number;
  scoreState?: Status | null;
  setIsAnnotationSelected?: (isSelected: boolean) => void;
  selectPreviousActiveAnnotation?: () => void;
  canvasContainer: RefObject<HTMLDivElement | null>;
  updateIsMouseScrollDisabled: (value: boolean) => void;
}

export interface AnnotationsOnPageManagerRef {
  rotateAnnotations: (coords: Coords, angle: rotateAngleValue) => void;
}

const AnnotationsOnPageManager = forwardRef<AnnotationsOnPageManagerRef | undefined, AnnotationsOnPageManagerProps>(
  (props, ref) => {
    const { onBeforeAddAnnotation, onAnnotationDeleted, onAnnotationsRefresh, updateIsMouseScrollDisabled } = props;
    const mouseKeyDownInProgress = useRef<boolean>(false);
    const mouseKeyAlreadyUpEvent = useRef<fabric.IEvent<MouseEvent> | null>(null);
    const { isUserKK, isUserKGZ, isUserCZE, isUserCZWO, isVerifier } = useSessionUser();
    const { openAlert, showLoader } = useLayoutContext();
    const {
      getNextScore,
      getPrevScore,
      updateScore,
      openScoreShelf,
      isCommentForCurrentScoreProvided,
      examTaskGroupSolutionWithTasks,
    } = useScoring();
    const {
      activeAnnotation,
      setActiveAnnotation,
      annotationVersions,
      activeAnnotationId,
      activeCriterion,
      activeTask,
      addingAnnotationEnabled,
      annotationDefinitions,
      annotationImages,
      annotations,
      annotationsVisible,
      canEditRemoveAnnotation,
      canUserRemoveAnnotation,
      canvas,
      canvasElement,
      cursorDimensions,
      getCanvasPointsForAnnotation,
      isActiveAnnotationForGrading,
      pageImages,
      rotateAngle,
      setAnnotationImages,
      setLastAddedAnnotationId,
      lastAddedAnnotationId,
      currentCoords,
      unlockAllAnnotations,
      activeAnnotationsRef,
      lockAllAnnotations,
    } = useAnnotations();

    const [annotationActionMenuPosition, setAnnotationActionMenuPosition] = useState<
      AnnotationActionMenuPosition | undefined
    >(undefined);
    const [isMaxScorePromptVisible, setIsMaxScorePromptVisible] = useState<boolean>(false);
    const annotationsRendering = useRef<boolean>(false);
    const pendingAnnotationsRender = useRef<AnnotationOnImage[] | null>(null);
    const deletingAnnotationsIdsRef = useRef<Set<string>>(new Set());
    const [updateScoreModalOn, setUpdateScoreModalOn] = useState<boolean>(false);
    const [pendingAnnotationData, setPendingAnnotationData] = useState<{
      x: number;
      y: number;
      targetImageData: pageImageData;
      annotationDefinitionCore: AnnotationDefinitionCore;
      nextScore: number | null;
      width?: number;
      height?: number;
      angle?: number;
    } | null>(null);

    const { setCurrentCursor } = useAnnotationCursor({
      canAddAnnotation: props.canAddAnnotation,
    });

    const deactivateAnnotationIfStatic = (annotationDefinitionCore: AnnotationDefinitionCore) => {
      if (annotationDefinitionCore.annotationType !== AnnotationType.dynamic) {
        setActiveAnnotation(null);
        props.onAnnotationDeactivate();
        setTimeout(() => {
          canvas.discardActiveObject();
          canvas.renderAll();
        }, 0);
      }
    };

    const clearActiveAnnotationSelection = () => {
      canvas.discardActiveObject();
      canvas.renderAll();
      props.setIsAnnotationSelected?.(false);
    };

    const updateAnnotation = (updatedAnnotations: AnnotationOnImage[], changeLayers: boolean, isDynamic?: boolean) => {
      updateAnnotationOnOrgz(updatedAnnotations).then(
        (isUpdated: boolean) => {
          if (isUpdated) {
            if (!changeLayers) setLastAddedAnnotationId(updatedAnnotations[0].annotationId);
            setAnnotationActionMenuPosition(undefined);
            props.onAnnotationChanged(updatedAnnotations);
            return;
          }
          openAlert('save_error');
        },
        (error) => {
          if (axios.isAxiosError(error) && error.response?.status === 404) {
            onAnnotationsRefresh();
            return;
          }

          openAlert('save_error');
        },
      );
    };

    const updateAnnotationPositionOnOrgz = (annotationOnImage: AnnotationOnImage | null, image: fabric.Object) => {
      let annotationToUpdate: AnnotationOnImage | null = annotationOnImage;

      if (annotationToUpdate === null && image.left && image.top && activeAnnotationsRef.current) {
        const annotationId = image.name?.substring(ANNOTATION_OBJECT_NAME.length) ?? '';
        const activeAnnotation = activeAnnotationsRef.current.find((x) => x.annotation.annotationId === annotationId);
        if (!activeAnnotation) return;

        annotationToUpdate = createNewAnnotationPositionBasedOnImage(pageImages, image, activeAnnotation.annotation);
        if (!annotationToUpdate) return;
      }

      if (!annotationToUpdate) return;

      const annotationDefinitionCore = getAnnotationDefinitionCore(
        annotationToUpdate,
        activeTask,
        annotationVersions,
        annotationDefinitions,
      );

      if (annotationDefinitionCore?.annotationType === AnnotationType.dynamic) {
        setupDynamicAnnotation(annotationToUpdate, annotationDefinitionCore, cursorDimensions.current, image);
      }

      const isDynamic = annotationDefinitionCore?.annotationType === AnnotationType.dynamic;

      updateAnnotation([annotationToUpdate], false, isDynamic);
    };

    const removeAnnotation = useCallback(
      (annotation: AnnotationOnImage, examTaskGroupSolutionScoreVersionId?: string) => {
        if (deletingAnnotationsIdsRef.current.has(annotation.annotationId)) return;

        deletingAnnotationsIdsRef.current.add(annotation.annotationId);
        showLoader(true, 'Usuwanie adnotacji');
        deleteAnnotationOnOrgz(annotation.annotationId)
          .then(
            () => {
              if (annotation.forScoring) {
                onAnnotationsRefresh(examTaskGroupSolutionScoreVersionId);
                setAnnotationActionMenuPosition(undefined);
                canvas.discardActiveObject();
                canvas.renderAll();
                return;
              }
              onAnnotationDeleted(annotation);
              const annotationImageName = ANNOTATION_OBJECT_NAME + annotation.annotationId;
              const toDelete = annotationImages.find((item) => item.image.name === annotationImageName);
              if (toDelete) canvas.remove(toDelete.image);
              setAnnotationActionMenuPosition(undefined);
              canvas.discardActiveObject();
              canvas.renderAll();
            },
            () => openAlert('delete_error'),
          )
          .finally(() => {
            deletingAnnotationsIdsRef.current.delete(annotation.annotationId);
            showLoader(false);
          });
      },
      [showLoader, onAnnotationDeleted, onAnnotationsRefresh, annotationImages, canvas, openAlert],
    );

    const handleRemoveAnnotation = useCallback(
      (annotationId?: string) => {
        let annotation: AnnotationOnImage | null = null;
        if (!annotationActionMenuPosition && !annotationId) return;

        if (annotationId) {
          const foundAnnotation = activeAnnotationsRef.current.find((x) => x.annotation.annotationId === annotationId);
          if (!foundAnnotation) return;
          annotation = foundAnnotation.annotation;
        } else if (annotationActionMenuPosition && !annotationId) {
          annotation = annotationActionMenuPosition.annotation;
        } else {
          return;
        }
        if (!canUserRemoveAnnotation(annotation)) return;

        if (activeCriterion && activeCriterion.isScoringAnnotationRequired && annotation.forScoring) {
          const prevScore = getPrevScore();
          updateScore(
            prevScore,
            async (
              scoreCandidate: number | null,
              criterion: ExamScoringSchema,
              responseData: ExamTaskGroupSolutionScoreShelfResponse | undefined,
            ) => {
              const annotationToDelete = beforeRemove(annotation!, responseData, activeCriterion);
              if (annotationToDelete) {
                removeAnnotation(annotationToDelete, responseData?.examTaskGroupSolutionScoreVersionId);
              }
            },
          );
          return;
        }
        if (
          props.scoreState &&
          (props.scoreState === Status.Assessed || (props.scoreState === Status.Closed && (isUserKGZ || isUserKK)))
        ) {
          openScoreShelf(async (responseData: ExamTaskGroupSolutionScoreShelfResponse | undefined) => {
            const annotationToDelete = beforeRemove(annotation!, responseData, activeCriterion);
            if (annotationToDelete) {
              removeAnnotation(annotationToDelete, responseData?.examTaskGroupSolutionScoreVersionId);
            }
          });
          return;
        }
        removeAnnotation(annotation);
      },
      [
        annotationActionMenuPosition,
        activeAnnotationsRef,
        activeCriterion,
        getPrevScore,
        updateScore,
        openScoreShelf,
        removeAnnotation,
        canUserRemoveAnnotation,
        props.scoreState,
        isUserKGZ,
        isUserKK,
      ],
    );

    const onPageDownOrUp = useCallback(
      (img: fabric.Object | null, direction: 'up' | 'down') => {
        if (!canvas) return;

        const annotations = [...activeAnnotationsRef.current.map((x) => x.annotation)];
        let annotationId: string | null = null;
        if (img) annotationId = getAnnotationIdFromImg(img);
        else if (annotationActionMenuPosition) annotationId = annotationActionMenuPosition.annotation.annotationId;

        if (!annotationId) return;

        const updatedAnnotations = getAnnotationWithUpdatedLayers(annotationId, annotations, direction);

        canvas.discardActiveObject();
        updateAnnotation(updatedAnnotations, true, false);
      },
      [canvas, annotationActionMenuPosition],
    );

    const onDelete = useCallback(
      (annotationId: string) => {
        handleRemoveAnnotation(annotationId);
      },
      [handleRemoveAnnotation],
    );

    useAnnotationsKeyboard({
      canvas: canvas,
      canvasContainerRef: props.canvasContainer,
      updateDelay: ANNOTATION_UPDATE_KEYBOARD_DELAY,
      lastAddedId: lastAddedAnnotationId,
      setIsAnnotationSelected: props.setIsAnnotationSelected,
      onAnnotationPositionUpdate: updateAnnotationPositionOnOrgz,
      onEscKey: props.selectPreviousActiveAnnotation,
      onDeleteKey: onDelete,
      onPageDownOrUpKey: onPageDownOrUp,
    });

    const handleAnnotationRightClick = (event: fabric.IEvent, annotation: AnnotationOnImage, image: fabric.Object) => {
      if (canEditRemoveAnnotation && canUserRemoveAnnotation(annotation) && image) {
        if (!canvasElement.current) {
          throw new Error('empty canvas!');
        }
        const zoom = canvas.getZoom();
        const { hasControls, hasBorders, left, top } = image;
        image.set({
          hasControls: false,
          hasBorders: true,
        });
        canvas.setActiveObject(image);
        canvas.renderAll();
        const canvasOffset = fabric.util.getElementOffset(canvasElement.current);
        const menuCoords = event.absolutePointer
          ? props.getPopoverCoord(event.absolutePointer.x, event.absolutePointer.y, zoom, canvasOffset)
          : props.getPopoverCoord(left || 0, top || 0, zoom, canvasOffset);
        menuCoords &&
          setAnnotationActionMenuPosition({
            annotation,
            position: { left: menuCoords.x, top: menuCoords.y - props.topCanvasMargin },
            imageSettings: { hasControls: hasControls!, hasBorders: !hasBorders },
          });
      }
    };

    const { loadAndDrawAnnotationsAsync, createNewAnnotation, removeTempAnnotationFromCanvas } =
      useAnnotationsImageSetup({
        onAnnotationRightClick: handleAnnotationRightClick,
        onStoreAnnotation: (updatedAnnotation, isDynamic) => updateAnnotation([updatedAnnotation], false, isDynamic),
        isAnnotationActive: !!activeAnnotation,
      });

    const loadAndDrawAnnotationsRef = useRef(loadAndDrawAnnotationsAsync);

    useEffect(() => {
      loadAndDrawAnnotationsRef.current = loadAndDrawAnnotationsAsync;
    }, [loadAndDrawAnnotationsAsync]);

    const {
      handleMouseDown: handleDynamicCreationMouseDown,
      handleMouseMove: handleDynamicCreationMouseMove,
      handleMouseUp: handleDynamicCreationMouseUp,
    } = useDynamicCreaton({
      onAnnotationRightClick: handleAnnotationRightClick,
      onStoreAnnotation: (updatedAnnotation, isDynamic) => updateAnnotation([updatedAnnotation], false, isDynamic),
      isAnnotationActive: !!activeAnnotation,
    });

    const clearAnnotationImages = (): void => {
      removeAnnotationImagesFromCanvas(activeAnnotationsRef, annotationImages, canvas);
      setAnnotationImages([]);
    };

    const addAnnotation = (
      x: number,
      y: number,
      targetImageData: pageImageData,
      annotationDefinitionCore: AnnotationDefinitionCore,
      criterion: ExamScoringSchema,
      width?: number,
      height?: number,
      angle?: number,
    ): Promise<AnnotationOnImage> => {
      return new Promise((resolve, reject) => {
        lockAllAnnotations();
        const newAnnotation = createNewAnnotation(
          x,
          y,
          targetImageData,
          annotationDefinitionCore,
          criterion,
          width,
          height,
          angle,
        );
        if (!newAnnotation) {
          if (annotationDefinitionCore.annotationType === AnnotationType.dynamic) {
            removeTempAnnotationFromCanvas();
          }
          return;
        }
        showLoader(true);
        addAnnotationOnOrgz(criterion.partialScoreId, newAnnotation)
          .then(
            (response: AxiosResponse<string>) => {
              newAnnotation.annotationId = response.data;
              // Do not auto-select scoring annotations after insert;
              // keeping them selected blocks keyboard navigation in Assessment panel.
              setLastAddedAnnotationId(isVerifier || newAnnotation.forScoring ? null : response.data);
              props.onAddAnnotation(newAnnotation);
              clearActiveAnnotationSelection();
              resolve(newAnnotation);
            },
            () => {
              openAlert('save_error');
              if (annotationDefinitionCore.annotationType === AnnotationType.dynamic) {
                removeTempAnnotationFromCanvas();
              }
              reject();
            },
          )
          .finally(() => showLoader(false));
      });
    };

    const updateScoreAndAddAnnotation = async () => {
      if (pendingAnnotationData && activeCriterion) {
        const { x, y, targetImageData, annotationDefinitionCore, nextScore, width, height, angle } =
          pendingAnnotationData;

        await updateScore(nextScore, async (_, criterion: ExamScoringSchema) => {
          if (activeCriterion) {
            const result = await addAnnotation(
              x,
              y,
              targetImageData,
              annotationDefinitionCore,
              criterion,
              width,
              height,
              angle,
            );
            deactivateAnnotationIfStatic(annotationDefinitionCore);
            return result;
          } else {
            removeTempAnnotationFromCanvas();
            return false;
          }
        });

        setUpdateScoreModalOn(false);
        setPendingAnnotationData(null);

        if (props.onCommentChanged) {
          props.onCommentChanged();
        }
      }
    };

    const cancelAnnotationInsertion = () => {
      if (pendingAnnotationData) {
        removeTempAnnotationFromCanvas();
        setPendingAnnotationData(null);
      }
      setUpdateScoreModalOn(false);
    };

    const insertAnnotationOnCanvas = useCallback(
      (
        x: number,
        y: number,
        targetObject?: fabric.Object,
        width?: number,
        height?: number,
        angle?: number,
        page?: pageImageData | null | undefined,
      ) => {
        let newAnnotation = null;
        if (
          activeAnnotation &&
          activeCriterion &&
          cursorDimensions.current &&
          activeTask &&
          !activeTask.noAnswer &&
          addingAnnotationEnabled &&
          props.canAddAnnotation &&
          rotateAngle === 0
        ) {
          const nextScore = getNextScore();
          if (nextScore === null && activeCriterion.isScoringAnnotationRequired && isActiveAnnotationForGrading) {
            setIsMaxScorePromptVisible(true);
            removeTempAnnotationFromCanvas();
          } else {
            const targetImageData = getTargetPageImageToInsertAnnotation(page ? [page] : pageImages, targetObject);
            if (targetImageData) {
              const annotationDefinitionCore = convertAnnotationDefinitionToAnnotationDefinitionCore(activeAnnotation);
              if (activeCriterion.isScoringAnnotationRequired && isActiveAnnotationForGrading) {
                const prevScore = activeCriterion?.score;
                const isCommentForNewScoreRequired = activeCriterion?.examScoreValues.find(
                  (w) => w.score === nextScore,
                )?.isCommentRequired;

                if (
                  isCommentForCurrentScoreProvided &&
                  !isCommentForNewScoreRequired &&
                  prevScore !== null &&
                  (isUserCZE || isUserCZWO) &&
                  (examTaskGroupSolutionWithTasks?.examTaskGroupSolutionScoreState === Status.New ||
                    examTaskGroupSolutionWithTasks?.examTaskGroupSolutionScoreState === Status.Open)
                ) {
                  setPendingAnnotationData({
                    x,
                    y,
                    targetImageData,
                    annotationDefinitionCore,
                    nextScore,
                    width,
                    height,
                    angle,
                  });
                  setUpdateScoreModalOn(true);
                  return;
                } else {
                  updateScore(
                    nextScore,
                    async (newScore: number | null, criterion: ExamScoringSchema) => {
                      if (activeCriterion) {
                        const result = await addAnnotation(
                          x,
                          y,
                          targetImageData,
                          annotationDefinitionCore,
                          criterion,
                          width,
                          height,
                          angle,
                        );
                        deactivateAnnotationIfStatic(annotationDefinitionCore);
                        return result;
                      } else {
                        removeTempAnnotationFromCanvas();
                        return false;
                      }
                    },
                    () => {
                      removeTempAnnotationFromCanvas();
                    },
                  );
                }
              } else {
                if (props.scoreState && (props.scoreState === Status.Assessed || isUserKGZ || isUserKK || isVerifier)) {
                  openScoreShelf(async (responseData: ExamTaskGroupSolutionScoreShelfResponse | undefined) => {
                    let shelfActiveCriterion = null;
                    if (responseData && activeCriterion) {
                      const newActiveCriterion = getCriterionFromTasks(
                        responseData.examTasksCriteriaDto.examTasksCriteria,
                        activeCriterion.examScoringSchemaId,
                      );
                      if (newActiveCriterion) {
                        shelfActiveCriterion = newActiveCriterion;
                      }
                    }
                    newAnnotation = await addAnnotation(
                      x,
                      y,
                      targetImageData,
                      annotationDefinitionCore,
                      shelfActiveCriterion ?? activeCriterion,
                      width,
                      height,
                      angle,
                    );
                    deactivateAnnotationIfStatic(annotationDefinitionCore);
                  });
                } else {
                  newAnnotation = addAnnotation(
                    x,
                    y,
                    targetImageData,
                    annotationDefinitionCore,
                    activeCriterion,
                    width,
                    height,
                    angle,
                  ).then((result) => {
                    deactivateAnnotationIfStatic(annotationDefinitionCore);
                    return result;
                  });
                }
              }
            }
          }
        }
        return newAnnotation;
      },
      [
        activeAnnotation,
        activeCriterion,
        cursorDimensions,
        activeTask,
        addingAnnotationEnabled,
        props.canAddAnnotation,
        rotateAngle,
        getNextScore,
        isActiveAnnotationForGrading,
        removeTempAnnotationFromCanvas,
        pageImages,
        isCommentForCurrentScoreProvided,
        isUserCZE,
        isUserCZWO,
        examTaskGroupSolutionWithTasks,
        setPendingAnnotationData,
        updateScore,
        addAnnotation,
        deactivateAnnotationIfStatic,
        props.scoreState,
        isUserKGZ,
        isUserKK,
        isVerifier,
        openScoreShelf,
        setIsMaxScorePromptVisible,
      ],
    );

    const handleCanvasMouseMove = useCallback(
      (event: fabric.IEvent<MouseEvent>) => {
        if (!activeAnnotation || !activeCriterion || !activeTask || mouseKeyDownInProgress.current) {
          return;
        }

        if (activeAnnotation?.annotationType === AnnotationType.dynamic) {
          handleDynamicCreationMouseMove(event);
        }

        if (cursorDimensions.current && !activeTask?.noAnswer) {
          const canvasPoints = getCanvasPointsForAnnotation(event.e.x, event.e.y);
          const targetImageData: pageImageData | undefined = getTargetPageImage(
            pageImages,
            canvasPoints.x,
            canvasPoints.y,
          );
          setCurrentCursor(!targetImageData);
        }
      },
      [
        activeAnnotation,
        activeCriterion,
        activeTask,
        cursorDimensions,
        getCanvasPointsForAnnotation,
        handleDynamicCreationMouseMove,
        pageImages,
        setCurrentCursor,
      ],
    );

    const handleCanvasMouseUp = useCallback(
      (event: fabric.IEvent<MouseEvent>) => {
        mouseKeyAlreadyUpEvent.current = event;
        if (mouseKeyDownInProgress.current) {
          return;
        }
        if (event.button === MouseButtons.left) {
          let passPageToInsert = false;
          const pointer = canvas.getPointer(event.e);
          const page = getImageUnderPointer(pageImages, pointer);
          if (!page) {
            return;
          }
          if (
            event.target?.name &&
            event.target.name.substring(0, ANNOTATION_OBJECT_NAME.length) === ANNOTATION_OBJECT_NAME
          ) {
            passPageToInsert = true;
          }

          if (activeAnnotation?.annotationType === AnnotationType.dynamic) {
            const annotationCoordinates = handleDynamicCreationMouseUp(event);
            if (annotationCoordinates) {
              const { x, y, width, height, angle } = annotationCoordinates;
              insertAnnotationOnCanvas(
                x,
                y,
                event.target,
                Math.max(width, MINIMUM_ANNOTATION_SIZE),
                Math.max(height, MINIMUM_ANNOTATION_SIZE),
                angle,
                passPageToInsert ? page : null,
              );
              updateIsMouseScrollDisabled(false);
              return;
            }
          }
          insertAnnotationOnCanvas(event.e.x, event.e.y, event.target, undefined, undefined, undefined, page);
          updateIsMouseScrollDisabled(false);
        }
      },
      [
        activeAnnotation?.annotationType,
        canvas,
        handleDynamicCreationMouseUp,
        insertAnnotationOnCanvas,
        pageImages,
        updateIsMouseScrollDisabled,
      ],
    );

    const handleCanvasMouseDown = useCallback(
      async (event: fabric.IEvent<MouseEvent>) => {
        mouseKeyAlreadyUpEvent.current = null;
        mouseKeyDownInProgress.current = true;
        try {
          if (!activeAnnotation) {
            return;
          } else {
            lockAllAnnotations();
            setLastAddedAnnotationId(null);
          }
          onBeforeAddAnnotation();
          if (
            activeAnnotation.annotationType === AnnotationType.dynamic &&
            (await handleDynamicCreationMouseDown(event))
          ) {
            updateIsMouseScrollDisabled(true);
          }
        } finally {
          mouseKeyDownInProgress.current = false;
        }
        if (mouseKeyAlreadyUpEvent.current) {
          handleCanvasMouseMove(mouseKeyAlreadyUpEvent.current);
          handleCanvasMouseUp(mouseKeyAlreadyUpEvent.current);
        }
      },
      [
        activeAnnotation,
        onBeforeAddAnnotation,
        handleDynamicCreationMouseDown,
        lockAllAnnotations,
        setLastAddedAnnotationId,
        updateIsMouseScrollDisabled,
        handleCanvasMouseMove,
        handleCanvasMouseUp,
      ],
    );

    const annotationActionMenuItems: ActionMenuItem[] = [
      {
        text: 'Przesuń na wierzch',
        onClick: () => onPageDownOrUp(null, 'up'),
      },
      {
        text: 'Przesuń na spód',
        onClick: () => onPageDownOrUp(null, 'down'),
      },
      {
        text: 'Usuń adnotację',
        onClick: () => handleRemoveAnnotation(),
      },
    ];

    const renderAnnotations = useCallback(
      async (annotationsToRender: AnnotationOnImage[]) => {
        if (annotationsRendering.current) {
          pendingAnnotationsRender.current = annotationsToRender;
          return;
        }

        annotationsRendering.current = true;

        try {
          let currentAnnotations: AnnotationOnImage[] | null = annotationsToRender;

          while (currentAnnotations) {
            pendingAnnotationsRender.current = null;

            await loadAndDrawAnnotationsRef.current(currentAnnotations, activeAnnotationsRef);
            currentAnnotations = pendingAnnotationsRender.current;
          }
        } finally {
          annotationsRendering.current = false;
        }
      },
      [activeAnnotationsRef],
    );

    useEffect(() => {
      if (!props.pageAreAddedToCanvas || !props.examSheetPagesLoaded || !pageImages.length) {
        return;
      }

      if (annotationsVisible) {
        void renderAnnotations(annotations);
      } else {
        if (annotationsRendering.current) return;

        clearAnnotationImages();
      }
    }, [
      props.pageAreAddedToCanvas,
      activeTask?.taskId,
      props.examSheetPagesLoaded,
      annotationsVisible,
      annotations,
      activeCriterion?.examScoringSchemaId,
      renderAnnotations,
    ]);

    const rotateAnnotationImages = useCallback(
      (pageImageCoords: Coords, angle: rotateAngleValue) => {
        annotationImages.forEach((annotationImage: AnnotationImageData) => {
          rotateAnnotation(annotationImage, pageImageCoords, angle, currentCoords);
        });
      },
      [annotationImages, currentCoords],
    );

    useImperativeHandle(
      ref,
      () => ({
        rotateAnnotations: (coords: Coords, angle: rotateAngleValue) => {
          if (annotationImages.length) {
            rotateAnnotationImages(coords, angle);
          }
        },
      }),
      [annotationImages.length, rotateAnnotationImages],
    );

    useEffect(() => {
      setActiveAnnotation(null);
      props.onAnnotationDeactivate();
    }, [activeCriterion?.examScoringSchemaId]);

    useCanvasHandlers(
      {
        canvas,
        enabled: !!activeAnnotation,
        onEnableChange: (enabled) => {
          if (enabled) {
            lockAllAnnotations();
          }
        },
        onMouseDown: handleCanvasMouseDown,
        onMouseMove: handleCanvasMouseMove,
        onMouseUp: handleCanvasMouseUp,
      },
      [
        activeAnnotation,
        activeAnnotationId,
        activeCriterion,
        addingAnnotationEnabled,
        canvas,
        handleCanvasMouseDown,
        handleCanvasMouseMove,
        handleCanvasMouseUp,
        lockAllAnnotations,
        pageImages,
        props.canAddAnnotation,
        rotateAngle,
      ],
    );

    useEffect(() => {
      if (!activeAnnotationId) {
        setActiveAnnotation(null);
      } else {
        const annotationDefinition = annotationDefinitions.find(
          (item: AnnotationDefinition) => item.id === activeAnnotationId,
        );
        if (annotationDefinition) {
          setActiveAnnotation(annotationDefinition);
        }
      }
    }, [activeAnnotationId, annotationDefinitions]);

    const closeActionsMenu = () => {
      const image = canvas.getActiveObject();
      if (image && annotationActionMenuPosition?.imageSettings) {
        image.set({
          ...annotationActionMenuPosition.imageSettings,
        });
      }

      canvas.discardActiveObject();
      canvas.renderAll();
      setAnnotationActionMenuPosition(undefined);
    };

    useEffect(() => {
      if (activeAnnotation) {
        lockAllAnnotations();
      } else {
        canvas?.discardActiveObject();
        canvas?.renderAll();
        if (lastAddedAnnotationId) {
          unlockAllAnnotations();
        }
      }
    }, [activeAnnotation]);

    return (
      <>
        {annotationActionMenuPosition && (
          <ActionsMenuItems
            menuItems={annotationActionMenuItems}
            position={annotationActionMenuPosition.position}
            onCloseMenu={closeActionsMenu}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          />
        )}

        <Prompt
          type="confirmation"
          size="sm"
          open={updateScoreModalOn}
          onConfirm={updateScoreAndAddAnnotation}
          onCancel={cancelAnnotationInsertion}
          mainActionButtonText="TAK"
          cancelButtonText="NIE"
          isBold={true}
          mainText={
            'Zmieniłeś punktację dla kryterium. Aktualna ocena nie wymaga komentarza. Czy chcesz usunąć istniejący komentarz i zapisać zmiany?'
          }
        />

        {isMaxScorePromptVisible && (
          <Prompt
            type="error"
            open={true}
            mainText={
              'Nie można dodać kolejnej adnotacji ponieważ w wybranym kryterium została już przydzielona maksymalna liczba punktów.'
            }
            isBold={true}
            onConfirm={() => {
              setIsMaxScorePromptVisible(false);
            }}
          />
        )}
      </>
    );
  },
);

export default AnnotationsOnPageManager;
AnnotationsOnPageManager.displayName = 'AnnotationsOnPageManager';
